import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

type SearchResults = {
  documents: Array<{ id: number; title: string; status: string; project_id: number; project_name: string }>;
  tasks: Array<{ id: number; title: string; status: string; project_id: number; project_name: string }>;
  discussions: Array<{ id: number; message: string; project_id: number; project_name: string; user_name: string }>;
  meetings: Array<{ id: number; title: string; project_id: number; project_name: string }>;
  decisions: Array<{ id: number; summary: string; project_id: number; project_name: string }>;
  private_notes: Array<{ id: number; content: string; project_id: number | null }>;
};

export default function GlobalSearch({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  useEffect(() => {
    if (!q.trim()) {
      setResults(null);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get<{ results: SearchResults }>(
          `/search?q=${encodeURIComponent(q.trim())}`
        );
        setResults(r.results);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose} style={{ alignItems: 'flex-start', paddingTop: 60 }}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher partout : documents, tâches, discussions, décisions, notes…"
          style={{
            width: '100%',
            padding: '12px 14px',
            border: '1px solid var(--c-border)',
            borderRadius: 8,
            background: 'var(--c-bg)',
            fontSize: 16,
          }}
        />
        <div style={{ marginTop: 12, maxHeight: '60vh', overflowY: 'auto' }}>
          {loading && <div className="card-meta">Recherche…</div>}
          {!loading && !results && q.length === 0 && (
            <div className="card-meta">Tape quelque chose pour chercher.</div>
          )}
          {!loading && results && (
            <>
              <ResultGroup
                title="Documents"
                items={results.documents.map((d) => ({
                  key: `d-${d.id}`,
                  label: d.title,
                  sub: `${d.project_name} · ${d.status}`,
                  to: `/projets/${d.project_id}/documents/${d.id}`,
                }))}
                onNav={onClose}
              />
              <ResultGroup
                title="Tâches"
                items={results.tasks.map((t) => ({
                  key: `t-${t.id}`,
                  label: t.title,
                  sub: `${t.project_name} · ${t.status}`,
                  to: `/projets/${t.project_id}/taches`,
                }))}
                onNav={onClose}
              />
              <ResultGroup
                title="Discussions"
                items={results.discussions.map((d) => ({
                  key: `m-${d.id}`,
                  label: d.message.slice(0, 80),
                  sub: `${d.project_name} · ${d.user_name}`,
                  to: `/projets/${d.project_id}/communication`,
                }))}
                onNav={onClose}
              />
              <ResultGroup
                title="Réunions"
                items={results.meetings.map((m) => ({
                  key: `r-${m.id}`,
                  label: m.title,
                  sub: m.project_name,
                  to: `/projets/${m.project_id}/communication`,
                }))}
                onNav={onClose}
              />
              <ResultGroup
                title="Décisions"
                items={results.decisions.map((d) => ({
                  key: `dc-${d.id}`,
                  label: d.summary,
                  sub: d.project_name,
                  to: `/projets/${d.project_id}/communication`,
                }))}
                onNav={onClose}
              />
              <ResultGroup
                title="Notes privées"
                items={results.private_notes.map((n) => ({
                  key: `n-${n.id}`,
                  label: n.content.slice(0, 80),
                  sub: n.project_id ? 'dans un projet' : 'espace perso',
                  to: n.project_id ? `/projets/${n.project_id}/perso` : '/',
                }))}
                onNav={onClose}
              />
              {Object.values(results).every((a) => a.length === 0) && (
                <div className="card-meta">Aucun résultat.</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultGroup({
  title,
  items,
  onNav,
}: {
  title: string;
  items: Array<{ key: string; label: string; sub: string; to: string }>;
  onNav: () => void;
}) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="card-meta" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        {title}
      </div>
      {items.map((it) => (
        <Link
          key={it.key}
          to={it.to}
          onClick={onNav}
          style={{
            display: 'block',
            padding: '8px 10px',
            borderRadius: 6,
            textDecoration: 'none',
            color: 'var(--c-text)',
          }}
          className="search-result"
        >
          <div style={{ fontWeight: 600 }}>{it.label}</div>
          <div className="card-meta" style={{ fontSize: 12 }}>
            {it.sub}
          </div>
        </Link>
      ))}
    </div>
  );
}
